import React, { useEffect, useState, useMemo } from "react";
import PageHeader from "../components/PageHeader";
import { useLang, tr } from "../contexts/LangContext";
import api from "../lib/api";
import { Calendar as CalIcon, MapPin, FileText, X, Download } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Link } from "react-router-dom";

export default function Agenda() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/pdfs", { params: { category: "planning" } }).then((r) => setPdfs(r.data)).catch(() => {});
  }, []);

  const eventDates = useMemo(() => events.map((e) => new Date(e.date)), [events]);

  const filteredEvents = useMemo(() => {
    if (!selected) return events;
    return events.filter((e) => {
      const d = new Date(e.date);
      return d.toDateString() === selected.toDateString();
    });
  }, [events, selected]);

  const typeStyle = (type) =>
    type === "regatta"
      ? "bg-club-red text-white"
      : type === "stage"
      ? "bg-club-yellow text-navy"
      : "bg-navy text-white";

  return (
    <div data-testid="agenda-page">
      <PageHeader eyebrow={t.agenda.eyebrow} title={t.agenda.title} lead={t.agenda.lead} />

      <section className="pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Calendar */}
          <div className="lg:col-span-5">
            <div className="bg-sand p-6 border border-slate-200">
              <Calendar
                mode="single"
                selected={selected}
                onSelect={setSelected}
                modifiers={{ hasEvent: eventDates }}
                modifiersClassNames={{ hasEvent: "bg-club-yellow/30 font-semibold" }}
                className="w-full"
              />
            </div>
            <div className="flex flex-wrap gap-4 mt-6 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-navy" />
                <span className="text-slate-600">{t.agenda.legend.event}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-club-red" />
                <span className="text-slate-600">{t.agenda.legend.regatta}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-club-yellow" />
                <span className="text-slate-600">{t.agenda.legend.stage}</span>
              </div>
            </div>
          </div>

          {/* Events list */}
          <div className="lg:col-span-7">
            <div className="label-eyebrow mb-4">{selected ? selected.toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "numeric", month: "long", year: "numeric" }) : t.common.all}</div>
            {filteredEvents.length === 0 ? (
              <div className="bg-sand border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="agenda-no-events">
                {t.agenda.noEvents}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEvents.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setPopup(e)}
                    className="text-left w-full bg-sand p-5 border-l-4 border-club-yellow hover:bg-white hover:shadow-md transition-all"
                    data-testid={`agenda-event-${e.id}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${typeStyle(e.type)}`}>
                        {t.agenda.legend[e.type]}
                      </span>
                      <span className="text-xs text-slate-500 flex items-center gap-1"><CalIcon className="w-3 h-3" />{new Date(e.date).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</span>
                    </div>
                    <div className="font-serif text-2xl text-navy">{e[`title_${lang}`]}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-club-yellow" />{e.location}</div>
                    {e[`description_${lang}`] && (
                      <p className="text-sm text-slate-600 mt-3 line-clamp-3" data-testid={`agenda-event-desc-${e.id}`}>
                        {e[`description_${lang}`]}
                      </p>
                    )}
                    <div className="text-[10px] uppercase tracking-widest text-club-red mt-3">{tr("Voir le détail →")}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PDFs */}
      {pdfs.length > 0 && (
        <section className="py-16 bg-sand">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
            <h3 className="font-serif text-2xl text-navy mb-6">{t.agenda.downloadPlanning}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {pdfs.map((p) => (
                <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}/api${p.url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white p-5 border border-slate-200 hover:border-club-red transition-colors group" data-testid={`pdf-${p.id}`}>
                  <FileText className="w-6 h-6 text-club-red shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="text-sm text-navy group-hover:text-club-red">{p.title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">{p.category}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {popup && <EventPopup event={popup} onClose={() => setPopup(null)} t={t} lang={lang} />}
    </div>
  );
}

function EventPopup({ event, onClose, t, lang }) {
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    // Only "event" type (from /api/events) has its own documents; regattas use their own page.
    if (event.type === "event") {
      api.get(`/events/${event.id}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
    }
  }, [event]);

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="agenda-event-popup" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-8 relative my-12" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="agenda-popup-close">
          <X className="w-5 h-5" />
        </button>
        <div className="label-eyebrow text-club-red mb-2">{t.agenda.legend[event.type]}</div>
        <h2 className="font-serif text-3xl text-navy" data-testid="agenda-popup-title">{event[`title_${lang}`]}</h2>
        <div className="flex items-center gap-4 text-sm text-slate-500 mt-2">
          <span className="flex items-center gap-1.5"><CalIcon className="w-4 h-4 text-club-yellow" />{new Date(event.date).toLocaleDateString(lang === "en" ? "en-GB" : lang, { day: "numeric", month: "long", year: "numeric" })}</span>
          <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-club-yellow" />{event.location}</span>
        </div>
        <div className="divider-gold mt-5 mb-5" />

        {event[`description_${lang}`] && (
          <p className="text-slate-700 leading-relaxed whitespace-pre-line">{event[`description_${lang}`]}</p>
        )}

        {event.type === "event" && (
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-club-red font-semibold mb-3">Documents</div>
            {docs.length === 0 ? (
              <div className="text-xs text-slate-400 border border-dashed border-slate-300 p-4">{tr("Aucun document.")}</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {docs.map((d) => (
                  <li key={d.id} className="py-2 flex items-center justify-between gap-3" data-testid={`agenda-doc-${d.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-navy text-sm truncate">{d.title}</div>
                      <div className="text-xs text-slate-500">{d.doc_type}</div>
                    </div>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api${d.url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-club-red border border-club-red px-3 py-1.5 hover:bg-club-red hover:text-white" data-testid={`agenda-doc-dl-${d.id}`}>
                      <Download className="w-3.5 h-3.5" /> PDF
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {event.type === "event" && <VolunteerSection eventId={event.id} />}

        {event.link && (
          <Link to={event.link} className="inline-block mt-6 bg-club-red text-white px-6 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="agenda-popup-link">
            {t.common.readMore}
          </Link>
        )}
      </div>
    </div>
  );

function VolunteerSection({ eventId }) {
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/tasks`);
      setTasks(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [eventId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedTaskId) { setErr(tr("Choisissez une mission")); return; }
    setErr(""); setBusy(true); setSuccess("");
    try {
      await api.post(`/events/${eventId}/volunteer`, { ...form, task_id: selectedTaskId });
      setSuccess(`Merci ${form.name} ! Votre inscription est enregistrée. Le comité vous recontactera.`);
      setForm({ name: "", email: "", phone: "", notes: "" });
      setSelectedTaskId("");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || tr("Erreur lors de l'inscription"));
    } finally { setBusy(false); }
  };

  if (tasks.length === 0) return null;
  return (
    <div className="mt-8 bg-emerald-50 border border-emerald-200 p-5" data-testid="volunteer-section">
      <div className="text-xs uppercase tracking-widest text-emerald-800 font-semibold mb-3">{tr("Devenir bénévole sur cet événement")}</div>
      <p className="text-sm text-emerald-900 mb-4">Le club recherche des bénévoles pour les missions ci-dessous. Choisissez celle qui vous correspond.</p>

      <div className="space-y-2 mb-5">
        {tasks.map((tk) => {
          const full = tk.slots_left === 0;
          const checked = selectedTaskId === tk.id;
          return (
            <label key={tk.id} className={`flex items-start gap-3 p-3 bg-white border cursor-pointer ${checked ? "border-emerald-700 ring-1 ring-emerald-600" : "border-slate-200 hover:border-emerald-400"} ${full ? "opacity-50 cursor-not-allowed" : ""}`} data-testid={`vol-task-${tk.id}`}>
              <input type="radio" disabled={full} checked={checked} onChange={() => setSelectedTaskId(tk.id)} className="mt-1" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-navy">{tk.title}</span>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${full ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>
                    {tk.slots_taken}/{tk.slots_needed} {full ? "complet" : "places"}
                  </span>
                </div>
                {tk.start_time && <div className="text-xs text-slate-500 mt-0.5">⏰ {tk.start_time}</div>}
                {tk.description && <p className="text-xs text-slate-600 mt-1">{tk.description}</p>}
              </div>
            </label>
          );
        })}
      </div>

      {success ? (
        <div className="bg-emerald-700 text-white p-4 text-sm" data-testid="volunteer-success">{success}</div>
      ) : (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="volunteer-form">
          <input required placeholder={tr("Nom complet")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm" data-testid="vol-name" />
          <input required type="email" placeholder={tr("Email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm" data-testid="vol-email" />
          <input placeholder={tr("Téléphone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm" data-testid="vol-phone" />
          <input placeholder="Notes (allergies, dispo, etc.)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm" data-testid="vol-notes" />
          {err && <div className="md:col-span-2 text-red-600 text-sm" data-testid="vol-err">{err}</div>}
          <button type="submit" disabled={busy || !selectedTaskId} className="md:col-span-2 bg-emerald-700 text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-emerald-800 disabled:opacity-60" data-testid="vol-submit">
            {busy ? "Inscription…" : "Je me porte volontaire"}
          </button>
        </form>
      )}
    </div>
  );
}

}
